import React from 'react';
import cx from 'classnames';
import Url from '../../../constants/Url';
import Tooltip from '../../common/Tooltip';
import DownloadBar from 'epic-diesel-common/src/components/download-bar/DownloadBar';
import PLATFORM_MAPPING from '../../../constants/PlatformMapping';
import {getAuthPopup, listenAuthPopup} from '../../../utils/oauth';
import {getMessage} from '../../common/Message';
import {canRemoveChannel} from '../../../../shared/utils/ValidationUtils';
import {transformViolation, mapViolation} from '../../../../shared/utils/SocialUtils';

import {
    ChannelStatus,
    ErrorCode,
    SocialPlatform,
    SocialChannel as Channel,
    Affiliate
} from '../../../../shared/types';

import {
    Button,
    buttonTypes,
    downloads,
    Message,
    priority,
    Icon,
    svgs
} from 'epic-diesel-common/src';

//@ts-ignore
import styles from './SocialChannel.pcss';

export interface SocialChannelProps {
    channel: Channel;
    platform: SocialPlatform;
    isNewApplicant: boolean;
    enablePlatform: boolean;
    onDisconnect: (e, severConnection: boolean) => void;
    messages: any;
    configState: any;
    affiliate: Affiliate;
    getSocialChannels: VoidFunction;
    onAddChannel: (channelData) => void;
    updateChannelData: (channelData) => void;
    getOAuthLink: (platform: string) => void;
    sendBeginAuthChannelEvent: (platform: string) => void;
}

const CODES = {
    oauthSuccess: str => `epic.affiliate.social.channels.congratulationsSubscriber${str}.v2`,
    checking: 'epic.affiliate.social.channels.checking.v2',
    disconnect: 'epic.affiliate.common.disconnect.v2',
    connect: 'epic.affiliate.common.connect.v2',
    remove: 'epic.affiliate.common.remove.v2',
    disabled: 'epic.affiliate.social.channels.errors.disabled.v2',
    primaryChannel: 'epic.affiliate.social.channels.mainChannel.header.v2'
};

const SocialChannel: React.FC<SocialChannelProps> = props => {
    const {channel, platform, enablePlatform, messages} = props;

    const onAddChannel = (e, callback) => {
        e.preventDefault();
        e.stopPropagation();

        callback({
            platform,
            channelStatus: ChannelStatus.VALIDATING
        });

        const state = Math.random().toString(36);
        const popup = getAuthPopup({
            platform,
            redirectUrl: `${Url().GET_OAUTH_LINK}?platform=${platform}&state=${state}`,
            state: Math.random().toString(36)
        });

        if (!popup) return;

        props.sendBeginAuthChannelEvent(platform);
        listenAuthPopup(popup, platform);
    };

    const renderErrorMessage = (code: string = ErrorCode.SOCIAL_CHANNEL_SOMETHING_WRONG) => {
        const error = transformViolation(code, platform, props.configState);
        let messageText;

        if (typeof error === 'object') {
            messageText = <Message code={error.errorCode} args={error.messageVars} />;
        } else {
            messageText = <Message code={error} />;
        }

        return (
            <li key={code} className={styles.errorMessage}>
                {messageText}
            </li>
        );
    };

    const formatStatus = () => {
        const {channelStatus, followers} = props.channel || {};
        let authStatusText = null;
        let loaderStatus = downloads.states.ERROR;
        let loaderPercent = 100;

        switch (channelStatus) {
            case ChannelStatus.VALID: {
                loaderStatus = downloads.states.COMPLETED;
                authStatusText = (
                    <li className={styles.successMessage}>
                        <Message
                            code={CODES.oauthSuccess(followers > 1 ? 's' : '')}
                            args={[followers]}
                        />
                    </li>
                );
                break;
            }
            case ChannelStatus.VALIDATING: {
                loaderPercent = null;
                loaderStatus = downloads.states.ACTIVE_PLAY;
                authStatusText = (
                    <li>
                        <Message code={CODES.checking} />
                    </li>
                );
                break;
            }
            case ChannelStatus.INVALID:
            case ChannelStatus.ERROR:
            default: {
                const msgCodes = mapViolation(props.channel);
                if (msgCodes.includes(ErrorCode.SOCIAL_CHANNEL_ALREADY_ADDED)) {
                    loaderStatus = downloads.states.ACTIVE;
                }
                authStatusText = msgCodes.map(error => renderErrorMessage(error));
                break;
            }
        }

        return {
            loaderStatus,
            authStatusText,
            loaderPercent
        };
    };

    const renderActionButton = () => {
        const {channel, onDisconnect, affiliate} = props;
        const {oauthDetails, channelStatus} = channel || {};
        const {valid: isAuthenticated} = oauthDetails || {};

        let code;
        let onClick;

        if (!channel) {
            onClick = e => onAddChannel(e, props.onAddChannel);
            code = CODES.connect;
        } else {
            if (channelStatus === ChannelStatus.VALID || isAuthenticated) {
                onClick = e => onDisconnect(e, true);
                code = CODES.disconnect;
            } else if (channelStatus !== ChannelStatus.VALIDATING) {
                onClick = e => onAddChannel(e, props.updateChannelData);
                code = CODES.connect;
            } else {
                // cancel button
                return null;
            }
        }

        return (
            <div className={styles.connectButtonWrapper}>
                <Button priority={priority.TERTIARY} type={buttonTypes.BUTTON} onClick={onClick}>
                    <Message code={code} />
                </Button>

                {canRemoveChannel(affiliate, channel) && (
                    <Button
                        priority={priority.TERTIARY}
                        type={buttonTypes.BUTTON}
                        onClick={onDisconnect}>
                        <Message code={CODES.remove} />
                    </Button>
                )}
            </div>
        );
    };

    const renderChannelStatus = authStatusText => {
        if (!authStatusText) return null;

        return <ul className={styles.validationData}>{authStatusText}</ul>;
    };

    const renderChannelNew = React.useCallback(() => {
        const svgIcon = svgs[`SOCIAL_${platform.split('_')[0]}`] || svgs.EPIC;
        return (
            <div className={styles.channelContentBody}>
                <Icon icon={svgIcon} className={styles.icon} fontSize="large" />
                <div className={styles.text}>
                    <Message className={styles.channelUrl} code={PLATFORM_MAPPING[platform]} />
                    <div className={styles.primaryLabel} />
                </div>
                {enablePlatform ? (
                    renderActionButton()
                ) : (
                    <Tooltip
                        className={styles.tip}
                        placement="top"
                        title={
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: getMessage(messages, CODES.disabled)
                                }}
                            />
                        }>
                        <Icon icon={svgs.INFO} />
                    </Tooltip>
                )}
            </div>
        );
    }, [channel]);

    const renderChannel = (hasError = false) => {
        const {loaderStatus, loaderPercent, authStatusText} = formatStatus();
        const svgIcon = svgs[`SOCIAL_${platform.split('_')[0]}`] || svgs.EPIC;

        const iconStyles = channel.mainChannel
            ? styles[platform]
            : cx(styles.icon, styles.activeIcon);

        return (
            <>
                <div className={styles.channelContentHeader}>
                    {hasError && (
                        <Tooltip
                            className={styles.tip}
                            placement="top"
                            title={
                                <>
                                    <Message code={ErrorCode.SOCIAL_CHANNEL_MATCH_URL} />
                                    {renderChannelStatus(authStatusText)}
                                </>
                            }>
                            <Icon icon={svgs.INFO} />
                        </Tooltip>
                    )}
                </div>
                <div className={styles.channelContentBody}>
                    <Icon icon={svgIcon} className={iconStyles} fontSize="large" />
                    <div className={styles.text}>
                        {channel.link ? (
                            <a
                                className={styles.channelUrl}
                                href={channel.link}
                                target="__blank"
                                rel="noopener noreferrer">
                                {channel.link}
                            </a>
                        ) : (
                            <Message
                                className={styles.channelUrl}
                                code={PLATFORM_MAPPING[platform]}
                            />
                        )}
                        <div className={styles.primaryLabel}>
                            {channel.mainChannel && <Message code={CODES.primaryChannel} />}
                        </div>
                    </div>

                    {renderActionButton()}
                </div>
                <div className={styles.channelContentFooter}>
                    {renderChannelStatus(authStatusText)}
                </div>
                <DownloadBar
                    status={loaderStatus}
                    percent={loaderPercent}
                    className={cx(styles.channelProgress, {
                        [styles.channelProgressAlready]: loaderStatus === downloads.states.ACTIVE
                    })}
                />
            </>
        );
    };

    const {channelStatus} = channel || {};
    const hasError =
        channelStatus && ![ChannelStatus.VALID, ChannelStatus.VALIDATING].includes(channelStatus);

    const classes = cx(styles.channel, {
        [styles.disabled]: !enablePlatform,
        [styles.hasError]: hasError
    });

    return (
        <div className={classes}>
            <div className={styles.channelContent}>
                {channel ? renderChannel(hasError) : renderChannelNew()}
            </div>
        </div>
    );
};

export default React.memo(SocialChannel);
